import React, { useEffect, useState } from 'react';
import { Inputs, PackageType, BuildPayType, IndustryType, RevenueCadence, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
interface CalculatorInputsProps {
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
  onReset?: () => void;
  onReview?: () => void;
  showActionButtons?: boolean;
}
export function CalculatorInputs({
  inputs,
  onChange,
  onReset,
  onReview,
  showActionButtons = true
}: CalculatorInputsProps) {
  const [expandedSections, setExpandedSections] = useState({
    companyInfo: true,
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModel: false
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Handle numeric input changes - allow empty string, convert to number only when valid
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    // If empty, set to empty string to allow clearing
    if (value === '') {
      onChange({
        ...inputs,
        [field]: ''
      });
      return;
    }
    // Otherwise parse as float
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({
        ...inputs,
        [field]: numValue
      });
    }
  };
  // Update industry-specific defaults when industry changes
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    onChange({
      ...inputs,
      industry: newIndustry,
      ticket: defaults.ticket,
      gpm: defaults.gpm,
      salesFirst90Total: defaults.salesFirst90Total,
      salesVetMonthly: defaults.salesVetMonthly,
      revenueCadence: defaults.revenueCadence,
      residualTicket: defaults.residualTicket
    });
  };
  return <div className="bg-white border-2 border-gray-200 rounded p-4 md:p-6 shadow-lg max-w-4xl mx-auto" data-id="element-121" data-mp="16gaqryfxrfcb">
      <div className="space-y-4" data-id="element-122" data-mp="1ainwwnylhf9m">
        {/* Company Information - Single Row (No Collapsible) */}
        <div className="bg-gray-50 p-3 rounded border-2 border-gray-200" data-id="element-123" data-mp="wjfvdoeylv29">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-124" data-mp="11hpnfq5e0fjn">
            {/* Company Name */}
            <div data-id="element-125" data-mp="1p7bs2t4j3mz1">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-126" data-mp="1p6s8z9356sjf">
                Company Name
              </label>
              <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} placeholder="Enter company name" className="w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-127" data-mp="1u5vdmsf7mwqv" />
            </div>

            {/* Industry */}
            <div data-id="element-128" data-mp="1hxeedbtujhj">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-129" data-mp="1hdvatafxp1x">
                Industry
              </label>
              <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-130" data-mp="3snfa9cxj12su">
                <option value="" data-id="element-131" data-mp="39pchqo5rsnqa">Select</option>
                <option data-id="element-132" data-mp="39qntba5gizry">Windows</option>
                <option data-id="element-133" data-mp="39rp7volom4ef">Roofing</option>
                <option data-id="element-134" data-mp="3npdqepsa0zgb">Solar (Sales)</option>
                <option data-id="element-135" data-mp="3nn127uvxdsut">Solar (Vertical)</option>
                <option data-id="element-136" data-mp="3iprdkhgd1l81">Solar (Community)</option>
                <option data-id="element-137" data-mp="3is5a6uqov5hp">Pest</option>
                <option data-id="element-138" data-mp="12yda4jllohzo">Fiber</option>
                <option data-id="element-139" data-mp="12xbm7ejhk10j">Deregulated Energy</option>
                <option data-id="element-140" data-mp="y01zw7re85gx">Hardscaping</option>
                <option data-id="element-141" data-mp="xz0fvdseg8n2">Landscaping</option>
                <option data-id="element-142" data-mp="1t9zxpzuntty4">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Retention Modeling - Collapsible */}
        <div data-id="element-143" data-mp="1y9l7z6fa5oq5">
          <div className="flex items-center justify-between p-3 bg-purple-50 rounded cursor-pointer hover:bg-purple-100 transition-colors border-2 border-purple-200" onClick={() => toggleSection('retentionModel')} data-id="element-144" data-mp="1y9bfnc6rj5ha">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-145" data-mp="2c9drohse34om">
              Retention Modeling
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-146" data-mp="2h6meivohfww7">
              {expandedSections.retentionModel ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.retentionModel && <div className="mt-3 bg-purple-50 p-3 rounded border-2 border-purple-200" data-id="element-147" data-mp="2qx7aebcrunbf">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-148" data-mp="2vxv2z1gnfc7r">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-149" data-mp="2zxx2b3ywvtjv">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-150" data-mp="2zw2jpwb44diy">
                    Hired to Launched
                  </label>
                  <div className="w-56 relative" data-id="element-151" data-mp="k8otj3f7mka6">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-152" data-mp="fadkguwqodt0" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-153" data-mp="tay0xkf6lxjc">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-154" data-mp="1ly1zp7nolfmo">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-155" data-mp="3g65we6svpuv9">
                    Launched to Day 30
                  </label>
                  <div className="w-56 relative" data-id="element-156" data-mp="3kadjy0nlp4rw">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-157" data-mp="36hnwdrsfbrso" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-158" data-mp="2shwcjaf87h6r">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-159" data-mp="qp88wzyo6rqg">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-160" data-mp="10uq78ivhvxm0">
                    Day 30 to Day 90
                  </label>
                  <div className="w-56 relative" data-id="element-161" data-mp="vsr6zhv1xgta">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-162" data-mp="19ms7ag1njbvr" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-163" data-mp="1ojrdq0kroyon">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-164" data-mp="3qcpag7ho0bev">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-165" data-mp="ipn8tcfldt5p">
                    Day 90 to 1 Year
                  </label>
                  <div className="w-56 relative" data-id="element-166" data-mp="sg852t7smllg">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-167" data-mp="sghv2gsu8sr5" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-168" data-mp="doz81z1k9xya">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Sales Performance - Collapsible */}
        <div data-id="element-169" data-mp="1ub2u55iccqul">
          <div className="flex items-center justify-between p-3 bg-amber-50 rounded cursor-pointer hover:bg-amber-100 transition-colors border-2 border-amber-200" onClick={() => toggleSection('salesPerformance')} data-id="element-170" data-mp="1uakndey1yu8f">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-171" data-mp="1liiq1pja5u2d">
              Sales Performance
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-172" data-mp="1bkl0mypopr86">
              {expandedSections.salesPerformance ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.salesPerformance && <div className="mt-3 bg-amber-50 p-3 rounded border-2 border-amber-200" data-id="element-173" data-mp="12k6czsp9001i">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-174" data-mp="12m9aexsqcvg9">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-175" data-mp="30yq6efe2s77h">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-176" data-mp="2wyz8zsu1mstz">
                    Sales per 90 Day Rep
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesFirst90Total} onChange={e => handleNumericChange('salesFirst90Total', e.target.value)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-177" data-mp="1osz8neh2h3l7" />
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-178" data-mp="1jtmcg34b2k5y">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-179" data-mp="1jvz08wwqvoqx">
                    Monthly Sales per Veteran
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => handleNumericChange('salesVetMonthly', e.target.value)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-180" data-mp="rcabhy32api5" />
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-181" data-mp="2t3k9t4bkcmhm">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-182" data-mp="3gs3gjfeysr7l">
                    Pullthrough / Realization
                  </label>
                  <div className="w-56 relative" data-id="element-183" data-mp="3lp2a9wupuw59">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-184" data-mp="37qlt1bz89n8v" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-185" data-mp="1x1o12qpie0u">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Revenue & Margins - Collapsible */}
        <div data-id="element-186" data-mp="3k7wmntgwbraz">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100 transition-colors border-2 border-green-200" onClick={() => toggleSection('revenueMargins')} data-id="element-187" data-mp="3fbftulyr4ork">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-188" data-mp="36c2lxxqa088y">
              Revenue & Margins
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-189" data-mp="18im235b4wuxx">
              {expandedSections.revenueMargins ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.revenueMargins && <div className="mt-3 bg-green-50 p-3 rounded border-2 border-green-200" data-id="element-190" data-mp="2mex6p7uit3q6">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-191" data-mp="2rdqc0cznlof9">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-192" data-mp="2w7uhert9toe8">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-193" data-mp="308eg3ztht02h">
                    Revenue Cadence
                  </label>
                  <select value={inputs.revenueCadence} onChange={e => handleChange('revenueCadence', e.target.value as RevenueCadence)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-194" data-mp="23jyqy4uaospw">
                    <option value="one-time" data-id="element-195" data-mp="3svin2ac5bogw">One-Time Sale</option>
                    <option value="monthly" data-id="element-196" data-mp="3rf4xrpjb4vgl">Monthly Residual</option>
                    <option value="quarterly" data-id="element-197" data-mp="3rhhmcljkm42m">Quarterly Residual</option>
                  </select>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-198" data-mp="4ap3veiwt2ay">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-199" data-mp="48m25x8e9kqe">
                    {inputs.revenueCadence === 'one-time' ? 'Average Ticket (Revenue per Sale)' : 'Initial Sale Ticket'}
                  </label>
                  <div className="w-56 relative" data-id="element-200" data-mp="99rx81pj1k33">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-201" data-mp="nzfwyyi28aop">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 pl-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-202" data-mp="j27fz18necp1" />
                  </div>
                </div>

                {inputs.revenueCadence !== 'one-time' && <>
                    <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-203" data-mp="sw3gywy0ydrs">
                      <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-204" data-mp="su0gtk35r49c">
                        {inputs.revenueCadence === 'monthly' ? 'Monthly Residual Ticket' : 'Quarterly Residual Ticket'}
                      </label>
                      <div className="w-56 relative" data-id="element-205" data-mp="nue4rdz2fu24">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-206" data-mp="9y0qsetqqase">
                          $
                        </span>
                        <input type="number" min="0" step="10" value={inputs.residualTicket || 0} onChange={e => handleNumericChange('residualTicket', e.target.value)} className="w-full p-2 pl-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-207" data-mp="2glht38b2v976" />
                      </div>
                    </div>

                    {inputs.revenueCadence === 'monthly' && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-208" data-mp="2lmfctpbqkbas">
                        <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-209" data-mp="1f5k1eku7kt7i">
                          Monthly Attrition
                        </label>
                        <div className="w-56 relative" data-id="element-210" data-mp="3ltkemb2s3fqt">
                          <input type="number" min="0" max="100" step="1" value={inputs.monthlyAttrition || 2} onChange={e => handleNumericChange('monthlyAttrition', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-211" data-mp="3qrlvcpcqf2z4" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-212" data-mp="2k35lp53xi9gr">
                            %
                          </span>
                        </div>
                      </div>}

                    {inputs.revenueCadence === 'quarterly' && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-213" data-mp="208cmgmudf58e">
                        <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-214" data-mp="2e1bygeuy2hb7">
                          Quarterly Attrition
                        </label>
                        <div className="w-56 relative" data-id="element-215" data-mp="2a1l4k2a4fl2g">
                          <input type="number" min="0" max="100" step="1" value={inputs.quarterlyAttrition || 5} onChange={e => handleNumericChange('quarterlyAttrition', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-216" data-mp="1mafnxkwcwor3" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-217" data-mp="3eak0edgusv9o">
                            %
                          </span>
                        </div>
                      </div>}
                  </>}

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-218" data-mp="3syonruqupuys">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-219" data-mp="1qkc6883xpu6">
                    Gross Profit Margin
                  </label>
                  <div className="w-56 relative" data-id="element-220" data-mp="3o76mt6otvudt">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-221" data-mp="3o5vh31btock2" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-222" data-mp="22d4ggm9jtayh">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {showActionButtons && onReset && <>
            <div className="border-t-2 border-gray-200 pt-4" data-id="element-223" data-mp="1jeigxbtojrwo">
              <button onClick={onReset} className="w-full px-4 py-2.5 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-bold text-sm transition-colors active:translate-y-[1px]" data-id="element-224" data-mp="1jgd12nx4nfzp">
                Reset to Defaults
              </button>
            </div>

            <div className="text-sm text-gray-600 leading-relaxed border-t-2 border-gray-200 pt-4" data-id="element-225" data-mp="3ipa2h2vji8r5">
              LMS note: access to a Learning Management System is included in
              all packages. In Edge it's more "DIY reference"; in
              Advantage/Summit it reinforces hands-on execution.
            </div>
          </>}

        {onReview && <div className="border-t-2 border-gray-200 pt-4" data-id="element-226" data-mp="qh71hq7rljv">
            <button onClick={onReview} className="w-full py-3.5 md:py-4 rounded font-bold text-base md:text-lg transition-all shadow-lg bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer" data-id="element-227" data-mp="ffvgsqxpc4zs">
              Review Your Numbers →
            </button>
          </div>}
      </div>
    </div>;
}